<template>
  <b-card no-body>
    <!-- UNIFIED HEADER: Tabs + Filter icon + CC icon -->
    <div class="rs-header">
      <div class="rs-tab-buttons">
        <button
          class="rs-tab-btn"
          :class="{ active: activeTabIndex === 0 }"
          @click="activeTabIndex = 0"
        >
          <feather-icon
            icon="UsersIcon"
            size="14"
            class="mr-25"
          />
          Buyers
          <b-badge
            v-if="selectedBuyerCount"
            variant="primary"
            pill
            class="ml-50"
          >
            {{ selectedBuyerCount }}
          </b-badge>
        </button>
        <button
          class="rs-tab-btn"
          :class="{ active: activeTabIndex === 1 }"
          @click="activeTabIndex = 1"
        >
          <feather-icon
            icon="TruckIcon"
            size="14"
            class="mr-25"
          />
          Suppliers
          <b-badge
            v-if="selectedSupplierCount"
            variant="warning"
            pill
            class="ml-50"
          >
            {{ selectedSupplierCount }}
          </b-badge>
        </button>
      </div>
      <div class="rs-header-actions">
        <feather-icon
          v-b-tooltip.hover
          icon="FilterIcon"
          size="16"
          class="cursor-pointer"
          :class="showFilters ? 'text-primary' : 'text-secondary'"
          title="Toggle Filters"
          @click="showFilters = !showFilters"
        />
        <feather-icon
          v-b-tooltip.hover
          icon="CopyIcon"
          size="16"
          class="cursor-pointer"
          :class="includeCc ? 'text-success' : 'text-secondary'"
          title="Include CC"
          @click="$emit('update:includeCc', !includeCc)"
        />
      </div>
    </div>

    <!-- FILTERS (collapsible) -->
    <b-collapse :visible="showFilters">
      <!-- Buyer filters -->
      <div
        v-if="activeTabIndex === 0"
        class="px-1 pt-50 pb-25"
      >
        <b-row>
          <b-col sm="4">
            <b-form-group
              label="Search"
              label-size="sm"
              class="mb-50"
            >
              <b-form-input
                v-model="buyerFilters.identifier"
                size="sm"
                placeholder="Name / identifier..."
                @input="debouncedFetchBuyers"
              />
            </b-form-group>
          </b-col>
          <b-col sm="4">
            <b-form-group
              label="Discount"
              label-size="sm"
              class="mb-50"
            >
              <b-form-input
                v-model="buyerFilters.discount"
                size="sm"
                placeholder="e.g. 2, 3, 5..."
                @input="debouncedFetchBuyers"
              />
            </b-form-group>
          </b-col>
          <b-col
            sm="4"
            class="position-relative"
          >
            <b-form-group
              label="Area"
              label-size="sm"
              class="mb-50"
            >
              <custom-v-select
                v-model="selectedAreas"
                :options="areaOptions"
                label="text"
                placeholder="Select Area"
                multiple
                :close-on-select="false"
                :deselect-from-dropdown="true"
                @input="fetchBuyers"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            sm="4"
            class="position-relative"
          >
            <b-form-group
              label="Salesman"
              label-size="sm"
              class="mb-50"
            >
              <custom-v-select
                v-model="selectedSalesmans"
                :options="salesmanOptions"
                label="text"
                placeholder="Select Salesman"
                multiple
                :close-on-select="false"
                :deselect-from-dropdown="true"
                @input="fetchBuyers"
              />
            </b-form-group>
          </b-col>
          <b-col
            sm="3"
            class="position-relative"
          >
            <b-form-checkbox
              :checked="buyerFilters.is_active === 'true'"
              @change="
                buyerFilters.is_active =
                  buyerFilters.is_active === 'true' ? null : 'true';
                fetchBuyers();
              "
            >
              Active
            </b-form-checkbox>
            <b-form-checkbox
              :checked="buyerFilters.is_active === 'false'"
              @change="
                buyerFilters.is_active =
                  buyerFilters.is_active === 'false' ? null : 'false';
                fetchBuyers();
              "
            >
              Inactive
            </b-form-checkbox>
          </b-col>
          <b-col
            sm="3"
            class="position-relative"
          >
            <b-form-checkbox
              :checked="buyerFilters.due === 'true'"
              @change="
                buyerFilters.due = buyerFilters.due === 'true' ? null : 'true';
                fetchBuyers();
              "
            >
              Due
            </b-form-checkbox>
            <b-form-checkbox
              :checked="buyerFilters.due === 'false'"
              @change="
                buyerFilters.due =
                  buyerFilters.due === 'false' ? null : 'false';
                fetchBuyers();
              "
            >
              No Due
            </b-form-checkbox>
          </b-col>
          <b-col
            sm="2"
            class="position-relative"
          >
            <b-form-checkbox
              v-model="selectedPaymentModeValues"
              value="cash"
              @change="fetchBuyers"
            >
              Cash
            </b-form-checkbox>
            <b-form-checkbox
              v-model="selectedPaymentModeValues"
              value="credit"
              @change="fetchBuyers"
            >
              Credit
            </b-form-checkbox>
          </b-col>
        </b-row>
      </div>

      <!-- Supplier filters -->
      <div
        v-if="activeTabIndex === 1"
        class="px-1 pt-50 pb-25"
      >
        <b-row>
          <b-col sm="6">
            <b-form-group
              label="Search"
              label-size="sm"
              class="mb-50"
            >
              <b-form-input
                v-model="supplierFilters.identifier"
                size="sm"
                placeholder="Supplier identifier..."
                @input="debouncedFetchSuppliers"
              />
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group
              label="Company Group"
              label-size="sm"
              class="mb-50"
            >
              <custom-v-select
                v-model="selectedCompanyGroups"
                :options="companyGroupOptions"
                label="text"
                placeholder="Select Group"
                multiple
                :close-on-select="false"
                :deselect-from-dropdown="true"
                @input="fetchSuppliers"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </b-collapse>

    <b-alert
      v-if="loadingError"
      show
      variant="danger"
      class="mx-1 mb-50"
      dismissible
      @dismissed="loadingError = null"
    >
      {{ loadingError }}
    </b-alert>

    <div class="rs-body">
      <!-- LEFT: User list -->
      <div class="rs-list-panel">
        <div class="rs-list-header">
          <span
            class="font-small-2 font-weight-bold text-primary cursor-pointer"
            @click="toggleSelectAll"
          >
            {{ isAllSelected ? "Deselect All" : "Select All" }}
          </span>
          <span
            class="font-small-2 text-muted"
          >{{ filteredUsers.length }} found</span>
        </div>

        <div
          v-if="loading"
          class="text-center py-3"
        >
          <b-spinner
            small
            variant="primary"
          />
          <span class="ml-50 font-small-3 text-muted">Loading...</span>
        </div>

        <div
          v-else
          class="rs-user-list"
        >
          <div
            v-for="user in filteredUsers"
            :key="user.uid"
            class="rs-user-item"
            :class="{
              'rs-user-selected': isSelected(user),
              'rs-user-buyer': user.userType === 'buyer',
              'rs-user-supplier': user.userType === 'supplier',
            }"
            @click="toggleUser(user)"
          >
            <div class="rs-select-indicator">
              <feather-icon
                v-if="isSelected(user)"
                icon="CheckIcon"
                size="13"
              />
            </div>
            <div class="flex-grow-1 min-width-0">
              <div class="font-small-3 font-weight-bolder text-truncate">
                {{ user.identifier || user.name }}
              </div>
              <div
                v-if="getParsedEmails(user.email).length"
                class="font-small-2 text-muted text-truncate"
              >
                {{ getParsedEmails(user.email).join(", ") }}
              </div>
            </div>
          </div>

          <div
            v-if="!filteredUsers.length && !loading"
            class="text-center py-3"
          >
            <span class="font-small-3 text-muted">
              No {{ activeTabIndex === 0 ? "buyers" : "suppliers" }} found
            </span>
          </div>
        </div>
      </div>

      <!-- RIGHT: Selected recipients -->
      <div class="rs-selected-panel">
        <div class="rs-selected-header">
          <span
            class="font-small-2 font-weight-bolder text-uppercase"
          >Selected</span>
          <b-badge
            variant="primary"
            pill
          >
            {{ selectedRecipients.length }}
          </b-badge>
          <div class="ml-auto d-flex align-items-center">
            <feather-icon
              icon="PlusCircleIcon"
              size="16"
              class="cursor-pointer text-primary mr-50"
              @click="openManualModal"
            />
            <a
              v-if="selectedRecipients.length"
              href="#"
              class="font-small-2 text-danger"
              @click.prevent="clearAll"
            >
              Clear
            </a>
          </div>
        </div>
        <div class="rs-selected-list">
          <div
            v-for="user in selectedRecipients"
            :key="user.uid"
            class="rs-selected-chip"
            :class="user.userType"
          >
            <div class="flex-grow-1 min-width-0">
              <div class="font-small-2 font-weight-bolder text-truncate">
                {{ user.identifier || user.name }}
              </div>
              <div
                v-if="getParsedEmails(user.email).length"
                class="font-small-1 text-muted text-truncate"
              >
                {{ getParsedEmails(user.email).join(", ") }}
              </div>
              <div
                v-if="includeCc && getParsedEmails(user.cc_emails).length"
                class="font-small-1 text-truncate"
                style="color: #ff9f43;"
              >
                CC: {{ getParsedEmails(user.cc_emails).join(", ") }}
              </div>
            </div>
            <feather-icon
              icon="XIcon"
              size="14"
              class="cursor-pointer text-muted flex-shrink-0"
              @click="removeRecipient(user)"
            />
          </div>

          <div
            v-if="!selectedRecipients.length"
            class="text-center py-3"
          >
            <feather-icon
              icon="UserMinusIcon"
              size="28"
              class="text-muted mb-50"
            />
            <div class="font-small-2 text-muted">
              No recipients selected
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MANUAL ADD MODAL -->
    <b-modal
      v-model="showManualModal"
      title="Add Recipient Manually"
      size="sm"
      centered
      ok-title="Add"
      @ok="addManualRecipient"
      @hidden="resetManualForm"
    >
      <b-form-group
        label="Name"
        label-size="sm"
      >
        <b-form-input
          v-model="manualForm.name"
          size="sm"
          placeholder="Recipient name"
        />
      </b-form-group>
      <b-form-group
        label="Email(s)"
        label-size="sm"
        description="Comma separated for multiple"
      >
        <b-form-input
          v-model="manualForm.emails"
          size="sm"
          placeholder="email@example.com"
        />
      </b-form-group>
      <b-form-group
        v-if="includeCc"
        label="CC Email(s)"
        label-size="sm"
        description="Comma separated for multiple"
      >
        <b-form-input
          v-model="manualForm.ccEmails"
          size="sm"
          placeholder="cc@example.com"
        />
      </b-form-group>
    </b-modal>
  </b-card>
</template>

<script>
import axios from 'axios'
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BBadge,
  BSpinner,
  BAlert,
  BCollapse,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'
import getParsedQueryString from '@/utils/headerQueryParser'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'

let buyerDebounce = null
let supplierDebounce = null

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BBadge,
    BSpinner,
    BAlert,
    BCollapse,
    BModal,
    CustomVSelect,
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  props: {
    selectedRecipients: {
      type: Array,
      default: () => [],
    },

    includeCc: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeTabIndex: 0,
      loading: false,
      loadingError: null,
      showFilters: false,

      buyerUsers: [],
      supplierUsers: [],

      buyerFilters: {
        identifier: '',
        is_active: null,
        discount: '',
        due: null,
      },

      supplierFilters: {
        identifier: '',
      },

      selectedAreas: [],
      selectedSalesmans: [],
      selectedCompanyGroups: [],
      selectedPaymentModeValues: [],

      areaOptions: [],
      salesmanOptions: [],
      companyGroupOptions: [],

      // Manual add modal
      showManualModal: false,
      manualForm: {
        name: '',
        emails: '',
        ccEmails: '',
      },
    }
  },

  computed: {
    filteredUsers() {
      return this.activeTabIndex === 0 ? this.buyerUsers : this.supplierUsers
    },

    selectedBuyerCount() {
      return this.selectedRecipients.filter(r => r.userType === 'buyer')
        .length
    },

    selectedSupplierCount() {
      return this.selectedRecipients.filter(r => r.userType === 'supplier')
        .length
    },

    isAllSelected() {
      return (
        this.filteredUsers.length > 0
        && this.filteredUsers.every(u => this.isSelected(u))
      )
    },

    isPartialSelected() {
      const count = this.filteredUsers.filter(u => this.isSelected(u)).length
      return count > 0 && count < this.filteredUsers.length
    },
  },

  watch: {
    activeTabIndex(val) {
      if (val === 0 && !this.buyerUsers.length) {
        this.fetchBuyers()
      }
      if (val === 1 && !this.supplierUsers.length) {
        this.fetchSuppliers()
      }
    },
  },

  mounted() {
    this.fetchFilterOptions()
    this.fetchBuyers()
  },

  methods: {
    /**
     * Parse email strings that come as Python-style lists: "['a@b.com', 'c@d.com']"
     * Also handles normal strings, arrays, and empty values.
     */
    getParsedEmails(raw) {
      if (!raw) return []
      if (Array.isArray(raw)) return raw.filter(Boolean)
      const str = String(raw).trim()
      if (str === '[]' || str === '') return []
      // Handle Python-style string list: "['email1', 'email2']"
      if (str.startsWith('[')) {
        try {
          // Replace single quotes with double quotes for JSON parse
          const jsonStr = str.replace(/'/g, '"')
          const parsed = JSON.parse(jsonStr)
          return Array.isArray(parsed) ? parsed.filter(Boolean) : []
        } catch {
          // Fallback: extract emails with regex
          const matches = str.match(/[\w.-]+@[\w.-]+\.\w+/g)
          return matches || []
        }
      }
      // Comma-separated string
      return str
        .split(',')
        .map(e => e.trim())
        .filter(Boolean)
    },

    /**
     * Build the final payload for sending emails.
     * Call this from the parent to get the recipients list.
     */
    getRecipientsPayload() {
      return this.selectedRecipients.map(user => {
        const toEmails = this.getParsedEmails(user.email)
        const entry = {
          to: toEmails,
          cc: [],
          name: user.identifier || user.name || '',
        }
        if (this.includeCc) {
          entry.cc = this.getParsedEmails(user.cc_emails)
        }
        return entry
      })
    },

    fetchFilterOptions() {
      axios.get('/accounts/areas_options').then(res => {
        this.areaOptions = res.data.data
      })

      axios.get('/accounts/salesmans_options').then(res => {
        this.salesmanOptions = res.data.data
      })

      axios.get('/company_group_options').then(res => {
        this.companyGroupOptions = res.data.data
      })
    },

    prepareBuyerPayload() {
      const payload = {}
      const f = this.buyerFilters
      if (f.identifier) payload.identifier = f.identifier
      if (f.is_active !== null) payload.is_active = f.is_active
      if (f.due !== null) payload.due = f.due
      if (f.discount) payload.discount = f.discount
      if (this.selectedAreas.length) {
        payload.area = this.selectedAreas.map(a => a.id)
      }
      if (this.selectedSalesmans.length) {
        payload.salesman = this.selectedSalesmans.map(s => s.id)
      }
      if (this.selectedPaymentModeValues.length) {
        payload.mode_of_payment = this.selectedPaymentModeValues
      }
      return payload
    },

    prepareSupplierPayload() {
      const payload = {}
      if (this.supplierFilters.identifier) payload.identifier = this.supplierFilters.identifier
      if (this.selectedCompanyGroups.length) {
        payload.company_group = this.selectedCompanyGroups.map(g => g.id)
      }
      return payload
    },

    async fetchBuyers() {
      this.loading = true
      this.loadingError = null
      try {
        const payload = this.prepareBuyerPayload()
        const response = await axios.get(
          `/emails/buyer_options_for_email?${getParsedQueryString(payload)}`,
        )
        const list = Array.isArray(response.data)
          ? response.data
          : response.data.results || []
        this.buyerUsers = list.map((u, i) => ({
          ...u,
          userType: 'buyer',
          uid: `buyer-${u.id || i}`,
        }))
      } catch (error) {
        this.loadingError = error?.response?.data?.error || 'Failed to fetch buyers'
        this.buyerUsers = []
      } finally {
        this.loading = false
      }
    },

    async fetchSuppliers() {
      this.loading = true
      this.loadingError = null
      try {
        const payload = this.prepareSupplierPayload()
        const response = await axios.get(
          `/emails/supplier_options_for_email?${getParsedQueryString(payload)}`,
        )
        const list = Array.isArray(response.data)
          ? response.data
          : response.data.results || []
        this.supplierUsers = list.map((u, i) => ({
          ...u,
          email: u.to_emails,
          userType: 'supplier',
          uid: `supplier-${u.id || i}`,
        }))
      } catch (error) {
        this.loadingError = error?.response?.data?.error || 'Failed to fetch suppliers'
        this.supplierUsers = []
      } finally {
        this.loading = false
      }
    },

    debouncedFetchBuyers() {
      clearTimeout(buyerDebounce)
      buyerDebounce = setTimeout(() => this.fetchBuyers(), 400)
    },

    debouncedFetchSuppliers() {
      clearTimeout(supplierDebounce)
      supplierDebounce = setTimeout(() => this.fetchSuppliers(), 400)
    },

    isSelected(user) {
      return this.selectedRecipients.some(r => r.uid === user.uid)
    },

    toggleUser(user) {
      const list = [...this.selectedRecipients]
      const idx = list.findIndex(r => r.uid === user.uid)
      if (idx > -1) {
        list.splice(idx, 1)
      } else {
        list.push({ ...user })
      }
      this.$emit('update:selectedRecipients', list)
    },

    toggleSelectAll() {
      const list = [...this.selectedRecipients]
      if (this.isAllSelected) {
        const uids = new Set(this.filteredUsers.map(u => u.uid))
        this.$emit(
          'update:selectedRecipients',
          list.filter(r => !uids.has(r.uid)),
        )
      } else {
        const existing = new Set(list.map(r => r.uid))
        const toAdd = this.filteredUsers.filter(u => !existing.has(u.uid))
        this.$emit('update:selectedRecipients', [
          ...list,
          ...toAdd.map(u => ({ ...u })),
        ])
      }
    },

    removeRecipient(user) {
      this.$emit(
        'update:selectedRecipients',
        this.selectedRecipients.filter(r => r.uid !== user.uid),
      )
    },

    clearAll() {
      this.$emit('update:selectedRecipients', [])
    },

    // --- Manual add ---
    openManualModal() {
      this.showManualModal = true
    },

    resetManualForm() {
      this.manualForm = { name: '', emails: '', ccEmails: '' }
    },

    addManualRecipient(bvModalEvent) {
      const name = this.manualForm.name.trim()
      const emails = this.manualForm.emails
        .split(',')
        .map(e => e.trim())
        .filter(Boolean)

      if (!name || !emails.length) {
        bvModalEvent.preventDefault()
        return
      }

      const ccEmails = this.manualForm.ccEmails
        .split(',')
        .map(e => e.trim())
        .filter(Boolean)

      const uid = `manual-${Date.now()}`
      const newUser = {
        uid,
        name,
        identifier: name,
        email: emails,
        cc_emails: ccEmails,
        userType: 'manual',
      }

      this.$emit('update:selectedRecipients', [
        ...this.selectedRecipients,
        newUser,
      ])
    },
  },
}
</script>

<style scoped>
.min-width-0 {
  min-width: 0;
}

/* Unified header row */
.rs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  border-bottom: 1px solid #ebe9f1;
}

.rs-tab-buttons {
  display: flex;
  flex: 1;
}

.rs-tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.6rem 0.75rem;
  border: none;
  background: transparent;
  font-size: 0.857rem;
  font-weight: 600;
  color: #6e6b7b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.15s ease;
  outline: none;
}

.rs-tab-btn:hover {
  color: #7367f0;
  background: #f8f8ff;
}

.rs-tab-btn.active {
  color: #7367f0;
  border-bottom-color: #7367f0;
}

.rs-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 0.75rem;
  border-left: 1px solid #ebe9f1;
  align-self: stretch;
}

/* Body layout */
.rs-body {
  display: flex;
  height: 340px;
  border-top: 1px solid #ebe9f1;
}

/* Left panel */
.rs-list-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ebe9f1;
  min-width: 0;
}

.rs-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f3f2f7;
  background: #f8f8f8;
}

.rs-user-list {
  flex: 1;
  overflow-y: auto;
}

/* Click-to-select items */
.rs-user-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f8f8;
  transition: all 0.15s ease;
  user-select: none;
}

.rs-user-item:hover {
  background: #f5f5f5;
}

/* Selected state — green highlight */
.rs-user-item.rs-user-selected {
  background: #e8f5e9;
  border-left: 3px solid #28c76f;
}

.rs-user-item.rs-user-selected .rs-select-indicator {
  background: #28c76f;
  color: #fff;
}

.rs-select-indicator {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  border: 2px solid #d4d4d4;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.rs-user-item:hover .rs-select-indicator {
  border-color: #28c76f;
}

/* Right panel — wider */
.rs-selected-panel {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fafafa;
}

.rs-selected-header {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.6rem;
  border-bottom: 1px solid #f3f2f7;
}

.rs-selected-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.35rem;
}

.rs-selected-chip {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  padding: 0.4rem 0.5rem;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.35rem;
  margin-bottom: 0.3rem;
}

.rs-selected-chip.buyer {
  border-left: 3px solid #7367f0;
}

.rs-selected-chip.supplier {
  border-left: 3px solid #ff9f43;
}

.rs-selected-chip.manual {
  border-left: 3px solid #28c76f;
}
</style>
